import { ref } from 'vue'
import { getAuth } from 'firebase/auth'
import { collection, getDocs, getDoc, query, doc, where, writeBatch, serverTimestamp, FieldPath, deleteField, arrayUnion, arrayRemove } from 'firebase/firestore'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { isInternalEmail } from '@/utils/emailDomains'
import { inactiveCompanyIdsOf } from '@/utils/userCompanyStatus'
import {
  asCompanyRoles,
  clientTypeForRole,
  customRoleIdFor,
  resolveCompanyRole,
  resolveUserRole,
  ROLE_LABELS,
  type CompanyPermissions,
  type CompanyRole,
  type CompanyRoleEntry,
  type CompanyRoles,
  type ModulePermissions,
  type UserRole
} from '@/domain/types/Role'
import { buildRoleActor, samePermissions, type AssignableRole } from '@/domain/types/CustomRole'
import type { ViewPresetDocument } from '@/interfaces/firestore/fields-management'

export interface CompanyUser {
  uid: string
  name: string
  lastName: string
  email: string
  role: UserRole
  status: string
  /** Activo en la compañía activa (no está en inactiveCompanyIds). Distinto del `status` global heredado. */
  active: boolean
  lastLoginAt: Date | null
  /** Permisos del usuario en TODAS sus compañías; se conservan al guardar. */
  allPermissions: CompanyPermissions
  /** Permisos en la compañía activa. undefined = sin configurar, acceso completo. */
  permissions: ModulePermissions | undefined
  /** companyRoles en TODAS sus compañías; se conserva al guardar. */
  allCompanyRoles: CompanyRoles
  /** Ids de compañías del usuario (para decidir si se refleja el rol global, D4). */
  companyIds: string[]
  /** Rol personalizado en la compañía activa (solo si el rol efectivo es 'regular'). */
  customRoleId: string | null
}

export interface SetUserStatusResult {
  status: 'active' | 'inactive'
  changed: boolean
  authStatus: 'enabled' | 'disabled' | 'disabled_externally' | 'no_account'
}

const toDate = (value: any): Date | null => {
  if (!value) return null
  if (typeof value.toDate === 'function') return value.toDate()
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Los usuarios antiguos tenían permissions como array; se ignora. */
const asPermissionsMap = (value: any): CompanyPermissions =>
  value && !Array.isArray(value) && typeof value === 'object' ? value : {}

export function useCompanyUsers() {
  const authStore = useAuthStore()
  const companyStore = useCompanyStore()
  const db = FirebaseProvider.getInstance().getFirestore()

  const users = ref<CompanyUser[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const currentCompanyId = () => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId) throw new Error('No hay compañía seleccionada')
    return companyId
  }

  const fetchUsers = async () => {
    try {
      isLoading.value = true
      error.value = null

      const companyId = currentCompanyId()
      const snapshot = await getDocs(
        query(collection(db, 'Users'), where('companyIds', 'array-contains', companyId))
      )

      // El equipo de la plataforma no aparece en el listado de una compañía cliente;
      // solo se ven entre ellos.
      const viewerIsInternal = isInternalEmail(authStore.user?.email || '')

      users.value = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data()
          const allPermissions = asPermissionsMap(data.permissions)
          const allCompanyRoles = asCompanyRoles(data.companyRoles)
          const globalRole = resolveUserRole(data.role, data.clientType)
          const role = resolveCompanyRole(globalRole, allCompanyRoles, companyId) || 'regular'

          return {
            uid: docSnap.id,
            name: data.name || '',
            lastName: data.lastName || '',
            email: data.email || '',
            role,
            status: data.status || 'active',
            active: !inactiveCompanyIdsOf(data).includes(companyId),
            lastLoginAt: toDate(data.lastLoginAt),
            allPermissions,
            permissions: allPermissions[companyId],
            allCompanyRoles,
            companyIds: Array.isArray(data.companyIds)
              ? data.companyIds
              : (data.companiesRef || []).map((c: any) => c.id),
            customRoleId: customRoleIdFor(allCompanyRoles, companyId, role)
          }
        })
        .filter((user) => viewerIsInternal || !isInternalEmail(user.email))
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email))
    } catch (err: any) {
      error.value = err.message || 'Error cargando los usuarios'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const activeAdminCount = () => users.value.filter((u) => u.role === 'admin' && u.active).length

  /**
   * Asigna un rol (predefinido o personalizado) en la compañía activa. Solo el
   * admin; nadie puede cambiarse el rol a sí mismo ni dejar la compañía sin admins.
   */
  const changeRole = async (
    user: CompanyUser,
    target: AssignableRole,
    previous: AssignableRole
  ): Promise<void> => {
    if (!authStore.isAdminOrSuperAdmin) {
      throw new Error('Solo un administrador puede cambiar roles')
    }
    if (user.uid === authStore.user?.uid) {
      throw new Error('No puedes cambiar tu propio rol')
    }
    if (user.role === 'superadmin') {
      throw new Error('No se puede cambiar el rol de un superadmin')
    }
    if (!user.active) {
      throw new Error('Reactiva al usuario antes de cambiarle el rol')
    }
    if (user.role === 'admin' && target.id !== 'admin' && activeAdminCount() <= 1) {
      throw new Error('Es el único administrador de la compañía. Asigna otro antes de cambiarle el rol.')
    }

    const cid = currentCompanyId()
    const baseRole: CompanyRole = target.isPredefined ? (target.id as CompanyRole) : 'regular'
    // Sin claves undefined: Firestore las rechaza.
    const entry: CompanyRoleEntry = target.isPredefined
      ? { role: baseRole }
      : { role: 'regular', customRoleId: target.id }
    const companyRoles = { ...user.allCompanyRoles, [cid]: entry }
    const permissions = { ...user.allPermissions }
    delete permissions[cid]

    // El listado puede estar desactualizado: la pertenencia a una sola compañía
    // se decide con el documento fresco. Se escribe solo la llave de la compañía
    // (FieldPath, por si el id trae puntos) para no pisar lo que otro admin
    // haya cambiado en las demás.
    const fresh = await getDoc(doc(db, 'Users', user.uid))
    // Mismo conjunto que cuenta la regla: companyIds ∪ {companyId}.
    const freshData = fresh.exists() ? fresh.data() : undefined
    const freshIds = new Set<string>(
      freshData && Array.isArray(freshData.companyIds) ? freshData.companyIds : user.companyIds
    )
    if (freshData?.companyId) freshIds.add(freshData.companyId)
    const mirrorGlobal = freshIds.size <= 1

    const batch = writeBatch(db)
    batch.update(
      doc(db, 'Users', user.uid),
      new FieldPath('companyRoles', cid), entry,
      new FieldPath('permissions', cid), deleteField(),
      'updatedAt', new Date(),
      ...(mirrorGlobal ? ['role', baseRole, 'clientType', clientTypeForRole(baseRole)] : [])
    )
    batch.set(doc(collection(db, 'Companies', cid, 'roleHistory')), {
      type: 'user_role_changed',
      roleId: target.id,
      roleName: target.name,
      targetUser: { uid: user.uid, email: user.email },
      before: {
        roleId: previous.id,
        roleName: previous.name,
        hadIndividualPermissions: !!user.permissions
      },
      after: { roleId: target.id, roleName: target.name },
      actor: buildRoleActor(authStore.user, getAuth().currentUser?.uid),
      at: serverTimestamp()
    })
    await batch.commit()

    user.role = baseRole
    user.allCompanyRoles = companyRoles
    user.customRoleId = target.isPredefined ? null : target.id
    user.allPermissions = permissions
    user.permissions = undefined
  }

  /**
   * Guarda los permisos del usuario en la compañía activa. Si conserva todas las
   * acciones de todos los módulos disponibles, se borra el mapa: la ausencia
   * significa acceso completo, y así los módulos que se habiliten más adelante
   * quedan accesibles sin tener que reeditar a cada usuario.
   */
  const savePermissions = async (
    user: CompanyUser,
    permissions: ModulePermissions,
    hasEverything: boolean
  ): Promise<boolean> => {
    if (!authStore.isAdminOrSuperAdmin) {
      throw new Error('Solo un administrador puede cambiar permisos')
    }
    if (!user.active) {
      throw new Error('Reactiva al usuario antes de cambiarle los permisos')
    }
    const companyId = currentCompanyId()

    const before = user.permissions ?? null
    const after = hasEverything ? null : permissions
    // Sin cambios reales no se escribe nada ni queda rastro.
    if ((before === null && after === null)
      || (before !== null && after !== null && samePermissions(before, after))) {
      return false
    }

    const allPermissions = { ...user.allPermissions }

    if (hasEverything) delete allPermissions[companyId]
    else allPermissions[companyId] = permissions

    const batch = writeBatch(db)
    batch.update(doc(db, 'Users', user.uid), {
      permissions: allPermissions,
      updatedAt: new Date()
    })
    batch.set(doc(collection(db, 'Companies', companyId, 'roleHistory')), {
      type: 'user_permissions_changed',
      roleId: 'regular',
      roleName: ROLE_LABELS.regular,
      targetUser: { uid: user.uid, email: user.email },
      before: { permissions: before },
      after: { permissions: after },
      actor: buildRoleActor(authStore.user, getAuth().currentUser?.uid),
      at: serverTimestamp()
    })
    await batch.commit()

    user.allPermissions = allPermissions
    user.permissions = allPermissions[companyId]
    return true
  }

  /**
   * Desactiva o reactiva al usuario en la compañía activa a través de la Cloud
   * Function: guarda el estado y el rastro en roleHistory y, si no le queda ninguna
   * compañía activa, deshabilita su cuenta de Firebase Auth (al reactivar la habilita).
   */
  const setUserActive = async (user: CompanyUser, active: boolean): Promise<SetUserStatusResult> => {
    if (!authStore.isAdminOrSuperAdmin) {
      throw new Error('Solo un administrador puede activar o desactivar usuarios')
    }
    if (user.uid === authStore.user?.uid) {
      throw new Error('No puedes cambiar tu propio estado')
    }
    if (user.role === 'superadmin') {
      throw new Error('No se puede cambiar el estado de un superadmin')
    }
    if (!active && user.active && user.role === 'admin' && activeAdminCount() <= 1) {
      throw new Error('Es el único administrador activo de la compañía. Asigna otro antes de desactivarlo.')
    }

    const currentUser = getAuth().currentUser
    if (!currentUser) throw new Error('Sesión no válida')

    const token = await currentUser.getIdToken()
    const response = await fetch(`${import.meta.env.VITE_CF_BASE_URL}/SET_COMPANY_USER_STATUS`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ company: currentCompanyId(), uid: user.uid, status: active ? 'active' : 'inactive' })
    })

    const result = await response.json().catch(() => ({}))
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'No se pudo cambiar el estado del usuario')
    }

    user.active = result.status === 'active'
    return { status: result.status, changed: !!result.changed, authStatus: result.authStatus }
  }

  /**
   * Asigna (o quita, con null) la vista de embarques del usuario en la compañía activa. Un solo
   * writeBatch con los presets que cambian y la entrada user_view_changed de roleHistory.
   * Devuelve false si no había nada que escribir.
   */
  const saveAssignedView = async (
    user: CompanyUser,
    presets: ViewPresetDocument[],
    selectedViewId: string | null
  ): Promise<boolean> => {
    if (!authStore.isAdminOrSuperAdmin) {
      throw new Error('Solo un administrador puede asignar vistas')
    }
    if (!user.active) {
      throw new Error('Reactiva al usuario antes de cambiarle la vista')
    }

    const cid = currentCompanyId()
    const actorUid = getAuth().currentUser?.uid ?? authStore.user?.uid
    if (!actorUid) throw new Error('Sesión no válida')

    const previous = presets.find((p) => p.assignedUserIds?.includes(user.uid)) ?? null
    const next = selectedViewId ? presets.find((p) => p.id === selectedViewId) ?? null : null
    if (selectedViewId && !next) throw new Error('La vista seleccionada ya no existe')

    // Lo quita de todas las vistas donde estaba, salvo la elegida, y lo agrega a la elegida.
    const changes = presets.filter(
      (p) => (p.assignedUserIds?.includes(user.uid) ?? false) !== (p.id === selectedViewId)
    )
    if (changes.length === 0) return false
    if (changes.some((p) => p.metadata?.isSystem)) {
      throw new Error('No se pueden editar presets del sistema')
    }

    const batch = writeBatch(db)
    for (const p of changes) {
      batch.update(doc(db, 'Companies', cid, 'viewPresets', p.id), {
        assignedUserIds: p.id === selectedViewId ? arrayUnion(user.uid) : arrayRemove(user.uid),
        'metadata.updatedAt': serverTimestamp(),
        'metadata.updatedBy': actorUid
      })
    }
    // Si los ids son iguales (solo se limpia un preset duplicado) la vista efectiva no cambió.
    if ((previous?.id ?? null) !== (next?.id ?? null)) {
      batch.set(doc(collection(db, 'Companies', cid, 'roleHistory')), {
        type: 'user_view_changed',
        roleId: '',
        roleName: '',
        targetUser: { uid: user.uid, email: user.email },
        before: { viewId: previous?.id ?? null, viewName: previous?.name ?? null },
        after: { viewId: next?.id ?? null, viewName: next?.name ?? null },
        actor: buildRoleActor(authStore.user, getAuth().currentUser?.uid),
        at: serverTimestamp()
      })
    }
    await batch.commit()

    for (const p of changes) {
      const current = p.assignedUserIds ?? []
      p.assignedUserIds = p.id === selectedViewId
        ? [...current, user.uid]
        : current.filter((id) => id !== user.uid)
    }
    return true
  }

  return {
    users,
    isLoading,
    error,
    fetchUsers,
    changeRole,
    savePermissions,
    setUserActive,
    saveAssignedView
  }
}
